import { core } from '@icc/shared';
import { _, Common, parseDate } from '@icc/shared/helpers';
import { Currency, exchange, ExchangeRate, formatCurrency } from '@icc/shared/currency';
import { PriceModifier } from '../interfaces/price-modifier.interface';
import { TaxRate } from '../interfaces/tax-rate.interface';
import { SalesDocumentPosition } from '../models/sales-document-position.model';
import { SalesDocument } from '../models/sales-document.model';
import { TaxRatesService } from './tax-rates.service';
import { Configuration } from '@icc/configurator/shared';
import { Discount, PricePart } from '@icc/shared/types';
import { Promotion } from '../interfaces/promotion.interface';
import { endOfDay } from 'date-fns';

export class CalculateDiscountsAndMultipliersService {
    // eslint-disable-next-line max-statements
    static calculateDiscountsAndMultipliers(
        {
            document,
            position,
            type,
            discounts,
            exchangeRates,
            taxRate,
            canExceedMaxDiscount,
            promotions,
            revisions,
        }: {
            document: SalesDocument;
            position: SalesDocumentPosition;
            type: 'buy' | 'sales';
            discounts: PriceModifier[],
            exchangeRates: ExchangeRate[];
            taxRate?: TaxRate;
            canExceedMaxDiscount?: boolean;
            promotions: Promotion[];
            revisions?: {
                discounts: number;
                promotions: number;
            }
        }
    ) {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        let mainDiscount = null;
        let accessoriesDiscount: PriceModifier = null;
        const accessoryCategoryDiscounts: Record<number, PriceModifier> = {};
        let pleatedMosquitoScreenDiscount: PriceModifier = null;
        let maxDiscountExceeded = false;
        let updatedPositionConfiguration = false;

        if (!Common.isObject(position.configuration)) {
            position.configuration = core.parseJson(position.configuration);
        }
        if (position.type === 'complex') {
            mainDiscount = core.copy(discounts.find(
                (el) =>
                    el.model === 'Configurator.ComplexConfigurators' &&
                    this.matchesForeignKey(el.foreignKey, Number(position.configuration.configuratorId)) &&
                    (!el.dateFrom || el.dateFrom <= date) &&
                    (!el.dateTo || el.dateTo >= date)
            ));

        } else if (position.type === 'static') {
            mainDiscount = core.copy(discounts.find(
                (el) =>
                    el.model === 'Product.Products' &&
                    this.matchesForeignKey(el.foreignKey, Number(position.configuration.productId)) &&
                    (!el.dateFrom || el.dateFrom <= date) &&
                    (!el.dateTo || el.dateTo >= date)
            ));
        } else if (position.type === 'external_venetian') {
            mainDiscount = core.copy(discounts.find(
                (el) =>
                    el.model === 'ShutterFeatures.ShutterTypes' &&
                    this.matchesForeignKey(el.foreignKey, Number(position.configuration.shutterType?.id)) &&
                    (!el.dateFrom || el.dateFrom <= date) &&
                    (!el.dateTo || el.dateTo >= date)
            ));
            accessoriesDiscount = this.findBestMatchingDiscount(
                discounts,
                (el) =>
                    this.matchesModifierTarget(
                        el,
                        'ConfigurationFeatures.WindowAccessories',
                        0,
                        position,
                        true
                    ) &&
                    (!el.dateFrom || el.dateFrom <= date) &&
                    (!el.dateTo || el.dateTo >= date)
            );
        } else if (position.type === 'roller_shutter') {
            // For roller shutters, first try to find a discount with profile match (foreignKey2)
            // If not found, fall back to shutter type only discount
            const shutterTypeId = Number(position.configuration.shutterType?.id);
            const profileId = Number(position.configuration.profile?.id);
            const mosquitoScreenTypeIds = position.configuration.mosquitoScreens?.map(ms => Number(ms.typeId)) || [];
            mainDiscount = this.findBestDualScopedDiscount(
                discounts,
                'ShutterFeatures.ShutterTypes',
                shutterTypeId,
                'ConfigurationFeatures.Profiles',
                profileId,
                (el) => (!el.dateFrom || el.dateFrom <= date) && (!el.dateTo || el.dateTo >= date)
            );
            // If no profile-specific discount found, try shutter type only
            if (!mainDiscount) {
                mainDiscount = this.findBestMatchingDiscount(
                    discounts,
                    (el) =>
                        this.matchesModifierTarget(
                            el,
                            'ShutterFeatures.ShutterTypes',
                            shutterTypeId,
                            position,
                            false
                        ) &&
                        (!el.dateFrom || el.dateFrom <= date) &&
                        (!el.dateTo || el.dateTo >= date)
                );
            }
            accessoriesDiscount = this.findBestMatchingDiscount(
                discounts,
                (el) =>
                    this.matchesModifierTarget(
                        el,
                        'ConfigurationFeatures.WindowAccessories',
                        0,
                        position,
                        true
                    ) &&
                    (!el.dateFrom || el.dateFrom <= date) &&
                    (!el.dateTo || el.dateTo >= date)
            );
            for (const discount of discounts) {
                const categoryKey = this.getModifierTargetKey(
                    discount,
                    'ConfigurationFeatures.WindowAccessoriesCategories',
                    position
                );
                if (
                    categoryKey !== null &&
                    (!discount.dateFrom || discount.dateFrom <= date) &&
                    (!discount.dateTo || discount.dateTo >= date)
                ) {
                    const currentDiscount = accessoryCategoryDiscounts[categoryKey];
                    if (!currentDiscount || this.isMoreSpecificModifier(discount, currentDiscount)) {
                        accessoryCategoryDiscounts[categoryKey] = core.copy(discount);
                    }
                }

               if (discount.model === 'ShutterFeatures.ShutterTypes' &&
                    mosquitoScreenTypeIds.some(id => this.matchesForeignKey(discount.foreignKey, id)) &&
                    !discount.foreignKey2 &&
                    (!discount.dateFrom || discount.dateFrom <= date) &&
                    (!discount.dateTo || discount.dateTo >= date)
                ) {
                    pleatedMosquitoScreenDiscount = core.copy(discount);
                }
            }
        } else if (position.type === 'pleated_mosquito_screen') {
            mainDiscount = core.copy(discounts.find(
                (el) =>
                    el.model === 'ShutterFeatures.ShutterTypes' &&
                    this.matchesForeignKey(el.foreignKey, Number(position.configuration.shutterType?.id)) &&
                    (!el.dateFrom || el.dateFrom <= date) &&
                    (!el.dateTo || el.dateTo >= date)
            ));
        }
        const mainPromotion = this.getPositionPromotion(position, promotions);

        const positionPrice = position.basePriceValue;

        position.priceBeforePromotionsValue = TaxRatesService.recalcPrice(
            TaxRatesService.isGross(document.taxRateCalculationMethod || 'netSummary'),
                exchange(positionPrice, position.basePriceCurrency, document.priceCurrency || 'PLN', exchangeRates),
            position.basePriceIsGross,
            position.basePriceTaxRate,
            taxRate?.value ?? position.priceTaxRate
        );

        let promotion = 0;
        let promotionValue = 0;
        let promotionBaseValue = 0;

        if (mainPromotion && mainPromotion.type === 'percent') {
            promotion = mainPromotion.value;
            promotionValue = core.round(position.priceBeforePromotionsValue * mainPromotion.value / 100);
            promotionBaseValue = exchange(promotionValue, document.priceCurrency || 'PLN', position.basePriceCurrency, exchangeRates);
        }

        position.pricePromotions = [{
            name: mainPromotion?.name || _('SALES_DOCUMENT|Promocja'),
            basePrice: exchange(position.priceBeforePromotionsValue, document.priceCurrency || 'PLN', position.basePriceCurrency, exchangeRates),
            basePriceCurrency: position.basePriceCurrency,
            price: position.priceBeforePromotionsValue,
            promotion,
            baseValue: promotionBaseValue,
            value: promotionValue,
            promotionType: 'percent',
            external: false,
        }];

        if (position.priceBeforePromotionsValue !== null) {
            position.priceBeforeDiscountsValue = position.pricePromotions.reduce(
                (prev, curr) => prev - curr.value,
                position.priceBeforePromotionsValue,
            );
        } else {
            position.priceBeforeDiscountsValue = null;
        }

        if (position.type !== 'complex' && position.configuration?.priceSegments?.length) {
            const { positionDiscounts, maxDiscountExceeded: confMaxDiscountExceeded, priceAfterDiscountsValue } = this.updatePositionConfiguration(position.configuration, mainDiscount, accessoriesDiscount, accessoryCategoryDiscounts, pleatedMosquitoScreenDiscount, {
                document,
                position,
                side: type,
                exchangeRates,
                taxRate,
                canExceedMaxDiscount,
                promotion: mainPromotion,
            });
            position.priceDiscounts = positionDiscounts;
            maxDiscountExceeded = maxDiscountExceeded || confMaxDiscountExceeded;
            if (position.priceBeforeDiscountsValue !== null) {
                position.priceAfterDiscountsValue = priceAfterDiscountsValue;
            } else {
                position.priceAfterDiscountsValue = null;
            }
            updatedPositionConfiguration = true;
        } else{
            let discount = 0;
            let value = 0;
            let baseValue = 0;

            if (mainDiscount) {
                if (mainDiscount?.type === 'percent') {
                    discount = mainDiscount.value;
                    if (document.discountType === 'price') {
                        value = core.round(position.priceBeforeDiscountsValue * mainDiscount.value / 100);
                    } else {
                        value = core.round(core.round(position.priceBeforeDiscountsValue * position.quantity) * mainDiscount.value / 100);
                    }
                    baseValue = exchange(value, document.priceCurrency || 'PLN', position.basePriceCurrency, exchangeRates);
                } else {
                    value = exchange(mainDiscount.value, position.basePriceCurrency, document.priceCurrency || 'PLN', exchangeRates);
                    if (document.discountType === 'price') {
                        discount = core.round(value / position.priceBeforeDiscountsValue * 100);
                    } else {
                        discount = core.round(value / (position.priceBeforeDiscountsValue * position.quantity) * 100);
                    }
                }
            }
            const price = position.priceBeforeDiscountsValue *
                    (document.discountType === 'price'
                        ? 1
                        : position.quantity);
            position.priceDiscounts = position.priceDiscounts.filter(el => el.edited).map(el => {
                el.basePrice = exchange(price, document.priceCurrency || 'PLN', el.basePriceCurrency, exchangeRates);
                el.price = price;
                el.maxDiscount = mainDiscount?.maxValue;
                el.maxDiscountType = mainDiscount?.type ?? 'value';
                if (el.discountType === 'percent') {
                    if (el.maxDiscount !== null && el.discount > el.maxDiscount && !el.external) {
                        if (!canExceedMaxDiscount) {
                            el.discount = el.maxDiscount;
                        }
                        maxDiscountExceeded = true;
                    }
                    if (document.discountType === 'price') {
                        el.value = core.round(position.priceBeforeDiscountsValue * el.discount / 100);
                    } else {
                        el.value = core.round(core.round(position.priceBeforeDiscountsValue * position.quantity) * el.discount / 100);
                    }
                    el.baseValue = exchange(el.value, document.priceCurrency || 'PLN', el.basePriceCurrency, exchangeRates);
                } else {
                    if (el.maxDiscount !== null && el.baseValue > el.maxDiscount && !el.external) {
                        if (!canExceedMaxDiscount) {
                            el.baseValue = el.maxDiscount;
                        }
                        maxDiscountExceeded = true;
                    }
                    el.value = exchange(el.baseValue, el.basePriceCurrency, document.priceCurrency || 'PLN', exchangeRates);
                    if (document.discountType === 'price') {
                        el.discount = core.round(el.value / position.priceBeforeDiscountsValue * 100);
                    } else {
                        el.discount = core.round(el.value / (position.priceBeforeDiscountsValue * position.quantity) * 100);
                    }
                }
                return el;
            });
            if (!position.priceDiscounts.some(el => el.type === 'main')) {
                position.priceDiscounts.push({
                    name: _('SALES_DOCUMENT|Rabat główny'),
                    basePrice: exchange(price, document.priceCurrency || 'PLN', position.basePriceCurrency, exchangeRates),
                    basePriceCurrency: position.basePriceCurrency,
                    price,
                    discount,
                    maxDiscount: mainDiscount?.maxValue,
                    maxDiscountType: mainDiscount?.type ?? 'value',
                    baseValue,
                    value,
                    type: 'main',
                    discountType: mainDiscount?.type ?? 'value',
                    edited: false,
                    external: false,
                });
            }
            if (position.priceBeforeDiscountsValue !== null) {
                position.priceAfterDiscountsValue = position.priceDiscounts.reduce(
                    (prev, curr) => prev - curr.value,
                    position.priceBeforeDiscountsValue *
                        (document.discountType === 'price'
                            ? 1
                            : position.quantity)
                );
            } else {
                position.priceAfterDiscountsValue = null;
            }

            if (revisions) {
                position.pricesRevisions = {
                    ...position.pricesRevisions,
                    discounts: revisions.discounts,
                    promotions: revisions.promotions,
                };
            }
        }

        return { maxDiscountExceeded, updatedPositionConfiguration };
    }

    static getMultiplier(model: string, foreignKey: string | number, salesMultipliers: PriceModifier[], model2?: string, foreignKey2?: string | number): number {
        const multiplier = salesMultipliers.find(
            (el) => {
                const matchesModel1 = el.model === model && this.matchesForeignKey(el.foreignKey, Number(foreignKey));
                // If model2/foreignKey2 search params are provided, check them
                if (model2 !== undefined && foreignKey2 !== undefined) {
                    return (
                        matchesModel1 &&
                        el.model2 === model2 &&
                        this.matchesForeignKey2(el.foreignKey2, Number(foreignKey2))
                    ) || (
                        el.model === model2 &&
                        this.matchesForeignKey(el.foreignKey, Number(foreignKey2)) &&
                        el.model2 === model &&
                        this.matchesForeignKey2(el.foreignKey2, Number(foreignKey))
                    );
                }
                if (!matchesModel1) {
                    return false;
                }
                // If no model2 search params, match entries without model2 or with any model2
                return true;
            }
        );

        return multiplier?.value || 1;
    }

    static getSalesPrice(value: number, position: SalesDocumentPosition, charge: number, marketFactor: number, salesMultipliers: PriceModifier[]) {
        if (value === null) {
            return null;
        }
        if (!Common.isObject(position.configuration)) {
            position.configuration = core.parseJson(position.configuration);
        }
        let multiplier = 1;
        if (position.type === 'complex') {
            multiplier = this.getMultiplier('Configurator.ComplexConfigurators', Number(position.configuration.configuratorId), salesMultipliers) || 1;
        } else if (position.type === 'static') {
            multiplier = this.getMultiplier('Product.Products', Number(position.configuration.productId), salesMultipliers) || 1;
        }
        return value * multiplier * charge * marketFactor;
    }

    static getPositionMultiplier(position: SalesDocumentPosition, salesMultipliers: PriceModifier[]) {
        if (!Common.isObject(position.configuration)) {
            position.configuration = core.parseJson(position.configuration);
        }
        let multiplier = 1;
        if (position.type === 'complex') {
            multiplier = this.getMultiplier('Configurator.ComplexConfigurators', Number(position.configuration.configuratorId), salesMultipliers) || 1;
        } else if (position.type === 'static') {
            multiplier = this.getMultiplier('Product.Products', Number(position.configuration.productId), salesMultipliers) || 1;
        }
        return multiplier;
    }

    static getPositionPromotion(position: SalesDocumentPosition, promotions: Promotion[]): Promotion | undefined {
        const matchingPromotions = promotions.filter(el => {
            if (el.dateFrom && parseDate(el.dateFrom) > new Date()) {
                return false;
            }
            if (el.dateTo && endOfDay(parseDate(el.dateTo)) < new Date()) {
                return false;
            }

            if (position.type === 'static') {
                return el.model === 'Products' && (!el.record.length || el.record.map(Number).includes(+position.configuration?.productId));
            }

            if (position.type === 'complex') {
                return el.model === 'ComplexConfigurators' && (!el.record.length || el.record.map(Number).includes(+position.configuration?.configuratorId));
            }
            return false;
        }).sort((a, b) => b.priority - a.priority) // sort by priority descending;

        return matchingPromotions[0];
    }

    static updatePositionConfiguration(configuration: Configuration, mainDiscount, accessoriesDiscount, accessoryCategoryDiscounts, pleatedMosquitoScreenDiscount, {
        document,
        position,
        side,
        exchangeRates,
        taxRate,
        canExceedMaxDiscount,
        promotion,
    }: {
        document: SalesDocument;
        position: SalesDocumentPosition;
        side: 'buy' | 'sales';
        exchangeRates: ExchangeRate[];
        taxRate?: TaxRate;
        canExceedMaxDiscount?: boolean;
        promotion: Promotion
    }) {
        const positionsDiscounts: Record<string, Discount> = {
            main: {
                name: _('SALES_DOCUMENT|Rabat główny'),
                type: 'main',
                baseUnitPrices: [],
                basePrice: 0,
                basePriceCurrency: position.basePriceCurrency,
                price: 0,
                value: 0,
                discount: 0,
                maxDiscount: mainDiscount?.maxValue,
                maxDiscountType: mainDiscount?.type ?? 'percent',
                baseValue: 0,
                discountType: mainDiscount?.type ?? 'percent',
                edited: false,
                external: false,
            },
            accessories: {
                name: _('SALES_DOCUMENT|Rabat za akcesoria'),
                type: 'accessories',
                basePrice: 0,
                baseUnitPrices: [],
                basePriceCurrency: position.basePriceCurrency,
                price: 0,
                value: 0,
                discount: 0,
                maxDiscount: accessoriesDiscount?.maxValue,
                maxDiscountType: accessoriesDiscount?.type ?? 'percent',
                baseValue: 0,
                discountType: accessoriesDiscount?.type ?? 'percent',
                edited: false,
                external: false,
            },
        };

        const accessoryCategoryDiscountTpl: Discount = {
            name: _('SALES_DOCUMENT|Rabat za kategorię {categoryName}'),
            categoryId: 0,
            categoryName: '',
            type: 'accessoryCategory',
            basePrice: 0,
            baseUnitPrices: [],
            basePriceCurrency: position.basePriceCurrency,
            price: 0,
            value: 0,
            discount: 0,
            maxDiscount: 0,
            maxDiscountType: 'percent',
            baseValue: 0,
            discountType: 'percent',
            edited: false,
            external: false,
        };

        const mosquitoScreenPositionDiscount: Discount = {
            name: _('SALES_DOCUMENT|Rabat za moskitierę plisowaną'),
            type: 'pleatedMosquitoScreen',
            basePrice: 0,
            baseUnitPrices: [],
            basePriceCurrency: position.basePriceCurrency,
            price: 0,
            value: 0,
            discount: 0,
            maxDiscount: pleatedMosquitoScreenDiscount?.maxValue,
            maxDiscountType: pleatedMosquitoScreenDiscount?.type ?? 'percent',
            baseValue: 0,
            discountType: pleatedMosquitoScreenDiscount?.type ?? 'percent',
            edited: false,
            external: false,
        };

        const positionMainDiscount = position.priceDiscounts.find(el => el.type === 'main');
        const positionAccessoriesDiscount = position.priceDiscounts.find(el => el.type === 'accessories');
        const positionPleatedMosquitoScreenDiscount = position.priceDiscounts.find(el => el.type === 'pleatedMosquitoScreen');
        const positionAccessoryCategoriesDiscounts: Record<number, Discount> = {};
        for (const discount of position.priceDiscounts) {
            if ('categoryId' in discount && discount.type.startsWith('accessoryCategory') && discount.categoryId) {
                positionAccessoryCategoriesDiscounts[discount.categoryId] = discount;
            }
        }

        let maxDiscountExceeded = false;
        let priceAfterDiscountsValue = 0;

        const priceSegmentsById: Record<number, any> = {};
        configuration.priceSegments.forEach((segment) => {
            priceSegmentsById[segment.id] = segment;
        });

        configuration.priceParts.forEach((part) => {
            let discountToApply = 0;
            let discount = 0;
            let value = 0;
            let baseValue = 0;
            let type: 'percent' | 'value' = 'percent';
            let discountObject = accessoriesDiscount;
            let discountType = 'accessories';
            let positionDiscount = positionAccessoriesDiscount;
            const segment = priceSegmentsById[part.valueId];
            if (part.valueType === 'externalVenetianRaster' || part.valueType === 'rollerShutterRaster' || part.valueType === 'pleatedMosquitoScreenRaster') {
                discountObject = mainDiscount;
                discountType = 'main';
                positionDiscount = positionMainDiscount;
            }
            if (part.valueType === 'externalVenetianCoverSegments') {
                if (segment?.data?.type === 'blind_box') {
                    discountObject = mainDiscount;
                    discountType = 'main';
                    positionDiscount = positionMainDiscount;
                }
            }
            if (part.valueType === 'pleatedMosquitoScreenRaster' && pleatedMosquitoScreenDiscount && configuration.type === 'roller_shutter') {
                discountObject = pleatedMosquitoScreenDiscount;
                discountType = 'pleatedMosquitoScreen';
                positionDiscount = positionPleatedMosquitoScreenDiscount;

                if (!positionsDiscounts[discountType]) {
                    positionsDiscounts[discountType] = mosquitoScreenPositionDiscount;
                }
            }
            if (segment?.data?.accessoryCategoryId && 'dictionary' in configuration) {
                const dictionary = configuration.dictionary as any;
                const categoryDiscount = accessoryCategoryDiscounts[segment.data.accessoryCategoryId] || accessoryCategoryDiscounts[0];
                if (categoryDiscount) {
                    discountObject = categoryDiscount;
                    discountType = 'accessoryCategory_' + segment.data.accessoryCategoryId;
                    positionDiscount = positionAccessoryCategoriesDiscounts[segment.data.accessoryCategoryId];
                    if (!positionsDiscounts[discountType]) {
                        positionsDiscounts[discountType] = {
                            ...core.copy(accessoryCategoryDiscountTpl),
                            categoryId: segment.data.accessoryCategoryId,
                            categoryName: dictionary?.accessoryCategories?.[segment.data.accessoryCategoryId]?.name || '',
                            type: 'accessoryCategory',
                            maxDiscount: categoryDiscount?.maxValue,
                            maxDiscountType: categoryDiscount?.type ?? 'percent',
                            discountType: categoryDiscount?.type ?? 'percent',
                        };
                    }
                }
            }

            const partPrices: Partial<PricePart> = {
                priceBeforePromotions: 0,
                promotion: null,
                priceBeforeDiscounts: 0,
                discount: null,
                priceAfterDiscounts: 0,
            };
            if (part.unitPrice !== null) {
                partPrices.priceBeforePromotions = TaxRatesService.recalcPrice(
                    TaxRatesService.isGross(document.taxRateCalculationMethod || 'netSummary'),
                    exchange(part.unitPrice, position.basePriceCurrency, document.priceCurrency || 'PLN', exchangeRates),
                    configuration.priceIsGross ?? position.basePriceIsGross,
                    part.taxRate,
                    taxRate?.value ?? position.priceTaxRate
                );

                if (promotion) {
                    partPrices.promotion = {
                        name: promotion.name,
                        promotion: promotion.value,
                        baseValue: partPrices.priceBeforePromotions,
                        value: core.round(partPrices.priceBeforePromotions * promotion.value / 100),
                        type: 'main',
                        promotionType: promotion.type,
                        external: false,
                    };
                    partPrices.priceBeforeDiscounts = partPrices.priceBeforePromotions - partPrices.promotion.value;
                } else {
                    partPrices.priceBeforeDiscounts = partPrices.priceBeforePromotions;
                }
            }
            if (discountObject || positionDiscount) {
                type = discountObject?.type;
                if (positionDiscount?.edited) {
                    type = positionDiscount.discountType;
                }
                if (type) {
                    if (type === 'percent') {
                        discountToApply = positionDiscount?.edited ? positionDiscount.discount : discountObject.value;
                        if (discountObject && side !== 'buy' && discountObject.maxValue && discountObject.type === 'percent' && discountToApply > discountObject.maxValue && !positionDiscount?.external) {
                            if (!canExceedMaxDiscount) {
                                discountToApply = discountObject.maxValue;
                            }
                            maxDiscountExceeded = true;
                        }
                        discount = discountToApply;
                        value = core.round(core.round(partPrices.priceBeforeDiscounts) * discount / 100);
                        baseValue = exchange(value, document.priceCurrency || 'PLN', position.basePriceCurrency, exchangeRates);
                    } else {
                        discountToApply = positionDiscount?.edited ? positionDiscount.value : discountObject.value;
                        if (discountObject && side !== 'buy' && discountObject.maxValue && discountObject.type === 'value' && discountToApply > discountObject.maxValue && !positionDiscount?.external) {
                            if (!canExceedMaxDiscount) {
                                discountToApply = discountObject.maxValue;
                            }
                            maxDiscountExceeded = true;
                        }
                        baseValue = discountToApply;
                        value = exchange(discountToApply, position.basePriceCurrency, document.priceCurrency || 'PLN', exchangeRates);
                        discount = core.round(value / (partPrices.priceBeforeDiscounts) * 100);
                    }
                }
            }
            partPrices.discount = {
                name: positionsDiscounts[discountType].name,
                discount,
                baseValue,
                value,
                type: discountType,
                discountType: type ?? 'value',
                edited: positionDiscount?.edited ?? false,
                external: positionDiscount?.external ?? false,
            };
            if (partPrices.priceBeforeDiscounts !== null) {
                partPrices.priceAfterDiscounts = core.round(core.round(partPrices.priceBeforeDiscounts - value) * part.quantity);
                priceAfterDiscountsValue += partPrices.priceAfterDiscounts;
            }

            positionsDiscounts[discountType].baseUnitPrices.push({
                pricePartValueId: part.valueId,
                pricePartPercentId: part.percentId,
                unitPrice: partPrices.priceBeforeDiscounts,
                quantity: part.quantity,
            });
            positionsDiscounts[discountType].price += partPrices.priceBeforeDiscounts * part.quantity;
            positionsDiscounts[discountType].basePrice += partPrices.priceBeforeDiscounts * part.quantity;

            positionsDiscounts[discountType].price = core.round(positionsDiscounts[discountType].price);
            positionsDiscounts[discountType].basePrice = core.round(positionsDiscounts[discountType].basePrice);

            if (type === 'percent') {
                positionsDiscounts[discountType].value += (value * part.quantity);
                positionsDiscounts[discountType].discount = discount;
                positionsDiscounts[discountType].baseValue += (baseValue * part.quantity);
            } else {
                positionsDiscounts[discountType].value = (value * part.quantity);
                positionsDiscounts[discountType].baseValue = (baseValue * part.quantity);
                positionsDiscounts[discountType].discount = core.round(value / positionsDiscounts[discountType].price * 100);
            }

            positionsDiscounts[discountType].value = core.round(positionsDiscounts[discountType].value);
            positionsDiscounts[discountType].baseValue = core.round(positionsDiscounts[discountType].baseValue);

            positionsDiscounts[discountType].edited = positionsDiscounts[discountType].edited || partPrices.discount.edited;
            positionsDiscounts[discountType].external = positionsDiscounts[discountType].external || partPrices.discount.external;

            if (partPrices.priceBeforePromotions !== null) {
                partPrices.priceBeforePromotions = core.round(partPrices.priceBeforePromotions * part.quantity);
                partPrices.priceBeforeDiscounts = core.round(partPrices.priceBeforeDiscounts * part.quantity);
            }

            Object.assign(part, partPrices);
        });
        return { positionDiscounts: Object.values(positionsDiscounts), maxDiscountExceeded, priceAfterDiscountsValue };
    }

    static formatDiscount(value: number, discountType: Discount['discountType'], lang?: string, currency?: Currency) {
        if (discountType === 'value') {
            return formatCurrency(value, lang, currency);
        } else {
            return core.round(value) + '%';
        }
    }

    private static findBestMatchingDiscount(
        discounts: PriceModifier[],
        predicate: (discount: PriceModifier) => boolean
    ): PriceModifier | null {
        let bestDiscount: PriceModifier = null;

        for (const discount of discounts) {
            if (!predicate(discount)) {
                continue;
            }

            if (!bestDiscount || this.isMoreSpecificModifier(discount, bestDiscount)) {
                bestDiscount = discount;
            }
        }

        return bestDiscount ? core.copy(bestDiscount) : null;
    }

    private static findBestDualScopedDiscount(
        discounts: PriceModifier[],
        firstModel: string,
        firstId: number,
        secondModel: string,
        secondId: number,
        predicate: (discount: PriceModifier) => boolean
    ): PriceModifier | null {
        let bestDiscount: PriceModifier = null;

        for (const discount of discounts) {
            if (!predicate(discount) || !this.matchesDualScopedModifier(discount, firstModel, firstId, secondModel, secondId)) {
                continue;
            }

            if (!bestDiscount || this.isMoreSpecificModifier(discount, bestDiscount)) {
                bestDiscount = discount;
            }
        }

        return bestDiscount ? core.copy(bestDiscount) : null;
    }

    private static isMoreSpecificModifier(candidate: PriceModifier, current: PriceModifier): boolean {
        return this.getModifierSpecificity(candidate) > this.getModifierSpecificity(current);
    }

    private static getModifierSpecificity(discount: PriceModifier): number {
        let specificity = 0;

        if (discount.foreignKey !== 0) {
            specificity += 2;
        }

        if (discount.model2) {
            specificity += 2;
            if (discount.foreignKey2 === 0) {
                specificity += 1;
            } else if (discount.foreignKey2 !== null && discount.foreignKey2 !== undefined) {
                specificity += 2;
            }
        }

        return specificity;
    }

    private static matchesModifierTarget(
        discount: Pick<PriceModifier, 'model' | 'foreignKey' | 'model2' | 'foreignKey2'>,
        targetModel: string,
        targetId: number,
        position: SalesDocumentPosition,
        useWildcard = false
    ): boolean {
        return this.getModifierTargetKey(discount, targetModel, position, useWildcard) === targetId;
    }

    private static getModifierTargetKey(
        discount: Pick<PriceModifier, 'model' | 'foreignKey' | 'model2' | 'foreignKey2'>,
        targetModel: string,
        position: SalesDocumentPosition,
        useWildcard = false
    ): number | null {
        if (
            this.matchesModifierSide(discount.model, discount.foreignKey, targetModel, useWildcard)
            && this.matchesAdditionalModifierScope(discount.model2, discount.foreignKey2, position)
        ) {
            return discount.foreignKey;
        }

        if (
            this.matchesModifierSide(discount.model2, discount.foreignKey2, targetModel, useWildcard)
            && this.matchesAdditionalModifierScope(discount.model, discount.foreignKey, position)
        ) {
            return discount.foreignKey2;
        }

        return null;
    }

    private static matchesDualScopedModifier(
        discount: Pick<PriceModifier, 'model' | 'foreignKey' | 'model2' | 'foreignKey2'>,
        firstModel: string,
        firstId: number,
        secondModel: string,
        secondId: number
    ): boolean {
        return (
            this.matchesModifierSide(discount.model, discount.foreignKey, firstModel)
            && this.matchesModifierSide(discount.model2, discount.foreignKey2, secondModel)
            && discount.foreignKey === firstId
            && discount.foreignKey2 === secondId
        ) || (
            this.matchesModifierSide(discount.model, discount.foreignKey, secondModel)
            && this.matchesModifierSide(discount.model2, discount.foreignKey2, firstModel)
            && discount.foreignKey === secondId
            && discount.foreignKey2 === firstId
        );
    }

    private static matchesModifierSide(
        model: string | undefined,
        foreignKey: number | null | undefined,
        targetModel: string,
        useWildcard = false
    ): boolean {
        if (model !== targetModel) {
            return false;
        }

        if (useWildcard) {
            return foreignKey === 0;
        }

        return foreignKey !== null && foreignKey !== undefined;
    }

    private static matchesAdditionalModifierScope(
        model: string | undefined,
        foreignKey: number | null | undefined,
        position: SalesDocumentPosition
    ): boolean {
        if (!model) {
            return true;
        }

        const positionScopeId = this.getPositionScopeId(position, model);
        if (positionScopeId === undefined) {
            return false;
        }

        return this.matchesForeignKey2(foreignKey, positionScopeId);
    }

    private static getPositionScopeId(position: SalesDocumentPosition, model: string): number | undefined {
        switch (model) {
            case 'Product.Products':
                return Number(position.configuration?.productId) || undefined;
            case 'Configurator.ComplexConfigurators':
                return Number(position.configuration?.configuratorId) || undefined;
            case 'ShutterFeatures.ShutterTypes':
                return Number(position.configuration?.shutterType?.id) || undefined;
            case 'ConfigurationFeatures.Profiles':
                return Number(position.configuration?.profile?.id) || undefined;
            default:
                return undefined;
        }
    }

    /**
     * Helper to check if a price modifier matches the given foreign key.
     * foreignKey = 0 means "all entities" and matches any value.
     */
    private static matchesForeignKey(modifierForeignKey: number | null | undefined, targetId: number): boolean {
        if (modifierForeignKey === 0) {
            return true; // 0 means "all"
        }
        return modifierForeignKey === targetId;
    }

    /**
     * Helper to check if a price modifier matches the given foreign key 2.
     * foreignKey2 = 0 means "all entities" and matches any value.
     */
    private static matchesForeignKey2(modifierForeignKey2: number | null | undefined, targetId: number | null | undefined): boolean {
        if (modifierForeignKey2 === 0) {
            return true; // 0 means "all"
        }
        if (targetId === undefined || targetId === null) {
            return !modifierForeignKey2;
        }
        return modifierForeignKey2 === targetId;
    }

}
